/**
 * Which network previews a post shows, as which connected account, with
 * which caption — the shared resolution behind the side-by-side preview set
 * (post detail + Approvals). Pure; unit tested in
 * `__tests__/lib/previewTargets.test.ts`.
 *
 * Tyler 2026-10-06: Facebook on the left, Instagram on the right, always
 * visible, no clicking. FB and IG always lead; any other network follows in a
 * fixed order.
 *
 * @module lib/previewTargets
 */

import type { Account, Platform } from "../types/social";
import { PLATFORM_LABELS } from "../types/social";

/** Display order: Facebook | Instagram first, then everything else. */
export const PREVIEW_ORDER: readonly Platform[] = [
  "facebook",
  "instagram",
  "linkedin",
  "threads",
  "bluesky",
  "tiktok",
  "pinterest",
  "google_business",
  "youtube",
];

export function isPreviewPlatform(value: unknown): value is Platform {
  return typeof value === "string" && value in PLATFORM_LABELS;
}

/** One post_platforms target (subset of `PostPlatformStatus`). */
export interface PreviewTarget {
  platform: string;
  account_id?: string | null;
  account_name?: string | null;
  adapted_content?: string | null;
}

export interface PreviewNetworkSources {
  /** posts.proposed_platforms (migration 087). */
  proposed?: readonly string[] | null;
  /** post_platforms rows (post detail) — or the queue's `platforms` list. */
  targets?: ReadonlyArray<PreviewTarget | string> | null;
  /** Autopilot planned_platforms. */
  planned?: readonly string[] | null;
  /** The brand's connected accounts — the fallback when nothing above is set. */
  connected?: ReadonlyArray<Pick<Account, "platform" | "status">> | null;
}

function ordered(set: Set<Platform>): Platform[] {
  return PREVIEW_ORDER.filter((p) => set.has(p));
}

/**
 * The networks to preview: proposed ∪ post_platforms ∪ planned; when that is
 * empty, the brand's connected (active) networks. Unknown names are dropped.
 */
export function previewNetworks(sources: PreviewNetworkSources): Platform[] {
  const picked = new Set<Platform>();
  const add = (p: unknown) => {
    if (isPreviewPlatform(p)) picked.add(p);
  };
  for (const p of sources.proposed ?? []) add(p);
  for (const t of sources.targets ?? []) add(typeof t === "string" ? t : t.platform);
  for (const p of sources.planned ?? []) add(p);
  if (picked.size === 0) {
    for (const a of sources.connected ?? []) {
      if (!a.status || a.status === "active") add(a.platform);
    }
  }
  return ordered(picked);
}

export interface PreviewIdentity {
  name: string;
  avatarUrl: string | null;
  /** Instagram-style @handle (no "@"). */
  handle: string;
}

function metaString(meta: unknown, key: string): string | null {
  if (!meta || typeof meta !== "object") return null;
  const v = (meta as Record<string, unknown>)[key];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** Fallback handle from a display name: lowercase, no whitespace. */
export function handleFromName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "");
}

/**
 * The connected account a network's preview is drawn as: the post's own
 * target account, else the brand's active account for that network, else
 * any brand account for it, else a name-only fallback.
 */
export function resolvePreviewAccount(
  platform: Platform,
  opts: {
    targets?: readonly PreviewTarget[] | null;
    accounts?: readonly Account[] | null;
    clientBrandId?: string | null;
    fallbackName?: string | null;
  }
): PreviewIdentity {
  const accounts = opts.accounts ?? [];
  const target = (opts.targets ?? []).find((t) => t.platform === platform);
  // Never borrow another brand's identity: a branded post only matches that
  // brand's accounts; an unbranded post only the org-level (unassigned) ones.
  const candidates = accounts.filter(
    (a) =>
      a.platform === platform && (opts.clientBrandId ? a.client_brand_id === opts.clientBrandId : !a.client_brand_id)
  );
  const account =
    (target?.account_id ? accounts.find((a) => a.id === target.account_id) : undefined) ??
    candidates.find((a) => a.status === "active") ??
    candidates[0];
  const name =
    account?.account_name?.trim() || target?.account_name?.trim() || opts.fallbackName?.trim() || "Your Account";
  const handle =
    (platform === "instagram"
      ? (metaString(account?.metadata, "ig_username") ?? metaString(account?.metadata, "username"))
      : null) ?? handleFromName(name);
  return { name, avatarUrl: account?.profile_image_url ?? null, handle };
}

/** Hashtags normalised to "#tag", minus any the caption already contains. */
export function withHashtags(text: string, hashtags?: readonly string[] | null): string {
  const lower = text.toLowerCase();
  const tags = (hashtags ?? [])
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => (t.startsWith("#") ? t : `#${t}`))
    .filter(
      (t, i, all) =>
        all.indexOf(t) === i && !new RegExp(`${escapeRe(t.toLowerCase())}(?![\\p{L}\\p{N}_])`, "u").test(lower)
    );
  if (!tags.length) return text;
  return text ? `${text}\n\n${tags.join(" ")}` : tags.join(" ");
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A network's caption as it will be sent: the target's adapted caption, else
 * the per-network override, else the shared caption — plus hashtags.
 */
export function previewCaption(
  platform: Platform,
  opts: {
    content: string;
    hashtags?: readonly string[] | null;
    targets?: readonly PreviewTarget[] | null;
    platformCaptions?: Record<string, string | null | undefined> | null;
  }
): string {
  const adapted = (opts.targets ?? []).find((t) => t.platform === platform)?.adapted_content;
  const override = opts.platformCaptions?.[platform];
  const base = adapted?.trim() ? adapted : override?.trim() ? override : opts.content;
  return withHashtags(base ?? "", opts.hashtags);
}

/** `metadata.platform_captions` as a plain string map (posts store overrides there). */
export function metadataPlatformCaptions(metadata: unknown): Record<string, string> {
  const raw = metadata && typeof metadata === "object" ? (metadata as Record<string, unknown>).platform_captions : null;
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (typeof v === "string") out[k] = v;
  return out;
}
